import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Lock, Mail, Loader2, ArrowLeft } from "lucide-react";
import { homeForRole, formatApiErrorDetail } from "@/components/schulungen/roles";

export default function SchulungenLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(homeForRole(user.role), { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(homeForRole(u.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, "Login fehlgeschlagen"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="schulungen-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-cyan-700 text-white flex items-center justify-center mx-auto mb-4"><GraduationCap className="h-7 w-7" /></div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Schulungsportal</h1>
          <p className="text-sm text-slate-500 mt-1">Login für Firmen, Teilnehmende & Kursleitung</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-xl border border-red-200" data-testid="schulungen-login-error">{error}</div>}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="schulungen-login-email" type="email" required placeholder="name@firma.de" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 pl-10 rounded-xl" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Passwort</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="schulungen-login-password" type="password" required placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pl-10 rounded-xl" />
              </div>
            </div>
            <Button type="submit" data-testid="schulungen-login-submit" disabled={loading} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anmelden"}
            </Button>
          </form>
          <p className="text-center text-sm mt-4"><Link to="/schulungen/passwort-vergessen" className="text-slate-500 hover:text-cyan-700 hover:underline" data-testid="login-forgot-link">Passwort vergessen?</Link></p>
          <p className="text-center text-sm text-slate-500 mt-4">
            Noch kein Firmenzugang? <Link to="/schulungen/firmenregistrierung" className="text-cyan-700 font-semibold hover:underline" data-testid="login-to-register-link">Firma registrieren</Link>
          </p>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          <Link to="/schulungen" className="inline-flex items-center gap-1 hover:text-cyan-700 transition-colors"><ArrowLeft className="h-3 w-3" /> Zurück zur Schulungsseite</Link>
        </p>
      </div>
    </div>
  );
}
